import { Injectable } from '@angular/core';
import { ApolloQueryResult, FetchResult } from '@apollo/client/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const NO_CACHE = true;

export interface ApiError {
  code: number;
  message: string;
}

interface Result<T> { result: T; }

function result<T>(res: ApolloQueryResult<Result<T>>): T {
  return res.data.result;
}

function fetch<T>(res: FetchResult<Result<T>>): T {
  return res.data.result;
}

function erorr(err: any): never {
  err = err.networkError ? (err.networkError.error || err.networkError) : err;
  err.code = err.code || err.status;
  throw err;
}

///////// API /////////
@Injectable()
export class AccessApi {
  constructor(private graphql: Apollo) { }

  public prepareSelfRegistration(customerId: string, refresh?: boolean): Observable<SelfRegistrationPrepareAdvice> {
    return this.graphql.query<Result<SelfRegistrationPrepareAdvice>>({
      query: gql`query request($customerId: String!) {
        result: AccessApi_prepareSelfRegistration(customerId: $customerId) {
          customerId # string,
          customerName # string,
          applicationId # string,
          applicationName # string,
          instanceId # string,
          instanceUrl # string,
          roleId # string
        }
      }`,
      variables: { customerId },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public selfRegistration(reg: SelfRegistrationRequest): Observable<SelfRegistrationResult> {
    return this.graphql.mutate<Result<SelfRegistrationResult>>({
      mutation: gql`mutation request($reg: SelfRegistrationRequest!) {
        result: AccessApi_selfRegistration(reg: $reg) {
          email # string,
          companyName # string
        }
      }`,
      variables: { reg }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public getUsersParameters(req: UsersAccessRequest, refresh?: boolean): Observable<UserAccessInfo[]> {
    return this.graphql.query<Result<UserAccessInfo[]>>({
      query: gql`query request($req: UsersAccessRequest!) {
        result: AccessApi_getUsersParameters(req: $req) {
          type # string,
          userId # string,
          objectId # string,
          userObjectId # string,
          principalName # string,
          companyId # string,
          companyName # string,
          displayName # string,
          firstName # string,
          lastName # string,
          email # string,
          companyPhone # string,
          address # string,
          postalCode # string,
          city # string,
          stateProvince # string,
          country # string,
          storeNumber # string,
          segment # string,
          jobTitle # string,
          division # string,
          manager # string,
          region # string,
          department # string,
          # grants # GrantAccessInfo[] ...
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public getUserParameters(req: UserAccessRequest, refresh?: boolean): Observable<UserAccessInfo> {
    return this.graphql.query<Result<UserAccessInfo>>({
      query: gql`query request($req: UserAccessRequest!) {
        result: AccessApi_getUserParameters(req: $req) {
          type # string,
          userId # string,
          objectId # string,
          userObjectId # string,
          principalName # string,
          companyId # string,
          companyName # string,
          displayName # string,
          firstName # string,
          lastName # string,
          email # string,
          companyPhone # string,
          address # string,
          postalCode # string,
          city # string,
          stateProvince # string,
          country # string,
          storeNumber # string,
          segment # string,
          jobTitle # string,
          division # string,
          manager # string,
          region # string,
          department # string,
          # grants # GrantAccessInfo[] ...
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public getCompaniesParameters(req: CompaniesAccessRequest, refresh?: boolean): Observable<CompanyAccessInfo[]> {
    return this.graphql.query<Result<CompanyAccessInfo[]>>({
      query: gql`query request($req: CompaniesAccessRequest!) {
        result: AccessApi_getCompaniesParameters(req: $req) {
          type # string,
          companyId # string,
          companyName # string,
          erpDataId # string,
          siteId # string,
          accountNumber # string,
          updated # Date,
          companyPhone # string,
          address # string,
          postalCode # string,
          city # string,
          stateProvince # string,
          country # string,
          # tenants # TenantAccessInfo[] ...
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public getCompanyParameters(req: CompaniesAccessRequest, refresh?: boolean): Observable<CompanyAccessInfo> {
    return this.graphql.query<Result<CompanyAccessInfo>>({
      query: gql`query request($req: CompaniesAccessRequest!) {
        result: AccessApi_getCompanyParameters(req: $req) {
          type # string,
          companyId # string,
          companyName # string,
          erpDataId # string,
          siteId # string,
          accountNumber # string,
          updated # Date,
          companyPhone # string,
          address # string,
          postalCode # string,
          city # string,
          stateProvince # string,
          country # string,
          # tenants # TenantAccessInfo[] ...
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class ApplicationApi {
  constructor(private graphql: Apollo) { }

  public search(req: ApplicationQueryInput, refresh?: boolean): Observable<ApplicationList> {
    return this.graphql.query<Result<ApplicationList>>({
      query: gql`query request($req: ApplicationQueryInput!) {
        result: ApplicationApi_search(req: $req) {
          query # any,
          items {
            applicationId # string,
            instanceId # string,
            name # string,
            instance # string,
            roles {
                roleId # string,
                name # string,
                description # string,
                companyType # string,
                customerAdmin # string
              },
            parameters {
                parameterId # string,
                applicationId # string,
                name # string,
                label # string,
                description # string,
                level # string,
                ordinal # number,
                status # string,
                parentId # string,
                type # string,
                options # string,
                minValue # number,
                maxValue # number,
                maxLength # number,
                defaultValue # string,
                expression # string,
                created # Date,
                updated # Date,
                version # number,
                # parent # ApplicationParameter ...,
                # children # ApplicationParameter[] ...,
                # application # Application ...,
                # tenantParameters # TenantParameter[] ...,
                # customerParameters # CustomerParameter[] ...,
                # nedermanParameters # NedermanParameter[] ...,
                value # string
              },
            customerCompanyCount # number,
            customerUserCount # number,
            nedermanUserCount # number
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public overview(req: ApplicationRequest, refresh?: boolean): Observable<ApplicationOverview> {
    return this.graphql.query<Result<ApplicationOverview>>({
      query: gql`query request($req: ApplicationRequest!) {
        result: ApplicationApi_overview(req: $req) {
          applicationId # string,
          name # string,
          sharedTenancy # string,
          multiSupplier # string,
          parameters {
            parameterId # string,
            applicationId # string,
            name # string,
            label # string,
            description # string,
            level # string,
            ordinal # number,
            status # string,
            parentId # string,
            type # string,
            options # string,
            minValue # number,
            maxValue # number,
            maxLength # number,
            defaultValue # string,
            expression # string,
            created # Date,
            updated # Date,
            version # number,
            # parent # ApplicationParameter ...,
            # children # ApplicationParameter[] ...,
            # application # Application ...,
            # tenantParameters # TenantParameter[] ...,
            # customerParameters # CustomerParameter[] ...,
            # nedermanParameters # NedermanParameter[] ...,
            value # string
          },
          created # Date,
          updated # Date,
          version # number,
          instance {
            instanceId # string,
            applicationId # string,
            parentId # string,
            name # string,
            type # string,
            nedermanTenant # string,
            customerAdmin # string,
            url # string,
            dataCenter # string,
            directoryId # string,
            clientId # string,
            enterpriseId # string,
            accessObjectId # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # directory # Directory ...,
            # tenants # Tenant[] ...,
            # nedermanGrants # NedermanGrant[] ...,
            # customerGrants # CustomerGrant[] ...,
            customerCompanyCount # number,
            customerUserCount # number,
            nedermanUserCount # number
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public fetch(req: ApplicationRequest, refresh?: boolean): Observable<SimpleApplicationItem[]> {
    return this.graphql.query<Result<SimpleApplicationItem[]>>({
      query: gql`query request($req: ApplicationRequest!) {
        result: ApplicationApi_fetch(req: $req) {
          applicationId # string,
          name # string
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class AuditApi {
  constructor(private graphql: Apollo) { }

  public search(req: AuditQueryInput, refresh?: boolean): Observable<AuditList> {
    return this.graphql.query<Result<AuditList>>({
      query: gql`query request($req: AuditQueryInput!) {
        result: AuditApi_search(req: $req) {
          query # any,
          items {
            created # Date,
            requestId # string,
            actionId # string,
            actorType # string,
            description # string,
            actorIpAddress # string,
            status # string
          },
          scope # boolean
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public csvReport(req: AuditQueryInput, refresh?: boolean): Observable<any> {
    return this.graphql.query<Result<any>>({
      query: gql`query request($req: AuditQueryInput!) {
        result: AuditApi_csvReport(req: $req) # : ANY
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class AzureApi {
  constructor(private graphql: Apollo) { }

  public listUsers(query: AzureUserQueryInput, refresh?: boolean): Observable<AzureUserQueryResult> {
    return this.graphql.query<Result<AzureUserQueryResult>>({
      query: gql`query request($query: AzureUserQueryInput!) {
        result: AzureApi_listUsers(query: $query) {
          query # any,
          users {
            userId # string,
            email # string,
            displayName # string,
            givenName # string,
            surname # string,
            accountEnabled # boolean,
            userPrincipalName # string,
            userType # string,
            initialPassword # string,
            deleted # boolean,
            debug # any
          },
          debug # any
        }
      }`,
      variables: { query },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public getUser(req: AzureUserIdInput, refresh?: boolean): Observable<AzureUserInfo> {
    return this.graphql.query<Result<AzureUserInfo>>({
      query: gql`query request($req: AzureUserIdInput!) {
        result: AzureApi_getUser(req: $req) {
          userId # string,
          email # string,
          displayName # string,
          givenName # string,
          surname # string,
          accountEnabled # boolean,
          userPrincipalName # string,
          userType # string,
          initialPassword # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public inviteUser(invite: AzureInviteInput): Observable<AzureInviteResult> {
    return this.graphql.mutate<Result<AzureInviteResult>>({
      mutation: gql`mutation request($invite: AzureInviteInput!) {
        result: AzureApi_inviteUser(invite: $invite) {
          userId # string,
          userPrincipalName # string,
          email # string,
          applicationId # string,
          displayName # string,
          sendInvitation # boolean,
          applicationUrl # string,
          redirectUrl # string,
          inviteId # string,
          redeemUrl # string,
          status # string,
          debug # any
        }
      }`,
      variables: { invite }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public createUser(user: AzureUserInput): Observable<AzureUserInfo> {
    return this.graphql.mutate<Result<AzureUserInfo>>({
      mutation: gql`mutation request($user: AzureUserInput!) {
        result: AzureApi_createUser(user: $user) {
          userId # string,
          email # string,
          displayName # string,
          givenName # string,
          surname # string,
          accountEnabled # boolean,
          userPrincipalName # string,
          userType # string,
          initialPassword # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { user }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public updateUser(user: AzureUserInput): Observable<AzureUserInfo> {
    return this.graphql.mutate<Result<AzureUserInfo>>({
      mutation: gql`mutation request($user: AzureUserInput!) {
        result: AzureApi_updateUser(user: $user) {
          userId # string,
          email # string,
          displayName # string,
          givenName # string,
          surname # string,
          accountEnabled # boolean,
          userPrincipalName # string,
          userType # string,
          initialPassword # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { user }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public deleteUser(user: AzureUserInput): Observable<AzureUserInfo> {
    return this.graphql.mutate<Result<AzureUserInfo>>({
      mutation: gql`mutation request($user: AzureUserInput!) {
        result: AzureApi_deleteUser(user: $user) {
          userId # string,
          email # string,
          displayName # string,
          givenName # string,
          surname # string,
          accountEnabled # boolean,
          userPrincipalName # string,
          userType # string,
          initialPassword # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { user }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public listGroups(dir: string, refresh?: boolean): Observable<AzureGroupResult[]> {
    return this.graphql.query<Result<AzureGroupResult[]>>({
      query: gql`query request($dir: String!) {
        result: AzureApi_listGroups(dir: $dir) {
          groupId # string,
          displayName # string,
          description # string,
          mailNickname # string,
          mailEnabled # boolean,
          mail # string,
          securityEnabled # boolean,
          createdDateTime # string,
          deletedDateTime # string,
          renewedDateTime # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { dir },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public getGroup(req: AzureGroupIdInput, refresh?: boolean): Observable<AzureGroupResult> {
    return this.graphql.query<Result<AzureGroupResult>>({
      query: gql`query request($req: AzureGroupIdInput!) {
        result: AzureApi_getGroup(req: $req) {
          groupId # string,
          displayName # string,
          description # string,
          mailNickname # string,
          mailEnabled # boolean,
          mail # string,
          securityEnabled # boolean,
          createdDateTime # string,
          deletedDateTime # string,
          renewedDateTime # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public createGroup(group: AzureGroupInput): Observable<AzureGroupResult> {
    return this.graphql.mutate<Result<AzureGroupResult>>({
      mutation: gql`mutation request($group: AzureGroupInput!) {
        result: AzureApi_createGroup(group: $group) {
          groupId # string,
          displayName # string,
          description # string,
          mailNickname # string,
          mailEnabled # boolean,
          mail # string,
          securityEnabled # boolean,
          createdDateTime # string,
          deletedDateTime # string,
          renewedDateTime # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { group }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public updateGroup(group: AzureGroupInput): Observable<AzureGroupResult> {
    return this.graphql.mutate<Result<AzureGroupResult>>({
      mutation: gql`mutation request($group: AzureGroupInput!) {
        result: AzureApi_updateGroup(group: $group) {
          groupId # string,
          displayName # string,
          description # string,
          mailNickname # string,
          mailEnabled # boolean,
          mail # string,
          securityEnabled # boolean,
          createdDateTime # string,
          deletedDateTime # string,
          renewedDateTime # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { group }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public deleteGroup(group: AzureGroupInput): Observable<AzureGroupResult> {
    return this.graphql.mutate<Result<AzureGroupResult>>({
      mutation: gql`mutation request($group: AzureGroupInput!) {
        result: AzureApi_deleteGroup(group: $group) {
          groupId # string,
          displayName # string,
          description # string,
          mailNickname # string,
          mailEnabled # boolean,
          mail # string,
          securityEnabled # boolean,
          createdDateTime # string,
          deletedDateTime # string,
          renewedDateTime # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { group }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public getGroupMembers(req: AzureGroupIdInput, refresh?: boolean): Observable<AzureUserInfo[]> {
    return this.graphql.query<Result<AzureUserInfo[]>>({
      query: gql`query request($req: AzureGroupIdInput!) {
        result: AzureApi_getGroupMembers(req: $req) {
          userId # string,
          email # string,
          displayName # string,
          givenName # string,
          surname # string,
          accountEnabled # boolean,
          userPrincipalName # string,
          userType # string,
          initialPassword # string,
          deleted # boolean,
          debug # any
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public addUserToGroup(assignment: AzureMembershipInput): Observable<AzureGroupMemberResult> {
    return this.graphql.mutate<Result<AzureGroupMemberResult>>({
      mutation: gql`mutation request($assignment: AzureMembershipInput!) {
        result: AzureApi_addUserToGroup(assignment: $assignment) {
          dir # string,
          userId # string,
          groupId # string,
          debug # any
        }
      }`,
      variables: { assignment }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public removeUserFromGroup(assignment: AzureMembershipInput): Observable<AzureGroupMemberResult> {
    return this.graphql.mutate<Result<AzureGroupMemberResult>>({
      mutation: gql`mutation request($assignment: AzureMembershipInput!) {
        result: AzureApi_removeUserFromGroup(assignment: $assignment) {
          dir # string,
          userId # string,
          groupId # string,
          debug # any
        }
      }`,
      variables: { assignment }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public assignAppToUser(assignment: AzureAssignmentInput): Observable<AzureAssignmentResult> {
    return this.graphql.mutate<Result<AzureAssignmentResult>>({
      mutation: gql`mutation request($assignment: AzureAssignmentInput!) {
        result: AzureApi_assignAppToUser(assignment: $assignment) {
          principalId # string,
          enterpriseId # string,
          assignmentId # string,
          principalDisplayName # string,
          appDisplayName # string,
          debug # any
        }
      }`,
      variables: { assignment }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public assignAppToGroup(assignment: AzureAssignmentInput): Observable<AzureAssignmentResult> {
    return this.graphql.mutate<Result<AzureAssignmentResult>>({
      mutation: gql`mutation request($assignment: AzureAssignmentInput!) {
        result: AzureApi_assignAppToGroup(assignment: $assignment) {
          principalId # string,
          enterpriseId # string,
          assignmentId # string,
          principalDisplayName # string,
          appDisplayName # string,
          debug # any
        }
      }`,
      variables: { assignment }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public removeAppFromUser(dismiss: AzureDismissInput): Observable<AzureDismissResult> {
    return this.graphql.mutate<Result<AzureDismissResult>>({
      mutation: gql`mutation request($dismiss: AzureDismissInput!) {
        result: AzureApi_removeAppFromUser(dismiss: $dismiss) {
          userId # string,
          assignmentId # string,
          debug # any
        }
      }`,
      variables: { dismiss }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class CustomerAccountApi {
  constructor(private graphql: Apollo) { }

  public search(req: CustomerAccountQueryInput, refresh?: boolean): Observable<CustomerAccountList> {
    return this.graphql.query<Result<CustomerAccountList>>({
      query: gql`query request($req: CustomerAccountQueryInput!) {
        result: CustomerAccountApi_search(req: $req) {
          query # any,
          items {
            accountId # string,
            nedermanId # string,
            siteId # string,
            accountNumber # string,
            name # string,
            city # string,
            country # string,
            vat # string,
            version # number,
            created # Date,
            updated # Date
          },
          scope # boolean
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public overview(req: CustomerAccountRequest, refresh?: boolean): Observable<CustomerAccountOverview> {
    return this.graphql.query<Result<CustomerAccountOverview>>({
      query: gql`query request($req: CustomerAccountRequest!) {
        result: CustomerAccountApi_overview(req: $req) {
          accountId # string,
          siteId # string,
          nedermanId # string,
          accountNumber # string,
          isBlocked # string,
          name # string,
          address # string,
          city # string,
          zipCode # string,
          country # string,
          state # string,
          area # string,
          address2 # string,
          city2 # string,
          zipCode2 # string,
          country2 # string,
          state2 # string,
          purchasingReference # string,
          telephone # string,
          fax # string,
          lineOfBusiness # string,
          salesResponsible # string,
          termsOfPayment # string,
          termsOfDelivery # string,
          language # string,
          currency # string,
          priceGroup # string,
          discountGroup # string,
          vatNo # string,
          deletedFromSource # string,
          lastModified # string,
          created # Date,
          updated # Date,
          version # number,
          company # boolean
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public prepareCreateCustomer(req: CustomerAccountRequest, refresh?: boolean): Observable<CustomerCompanyPrepareCreateAdvice> {
    return this.graphql.query<Result<CustomerCompanyPrepareCreateAdvice>>({
      query: gql`query request($req: CustomerAccountRequest!) {
        result: CustomerAccountApi_prepareCreateCustomer(req: $req) {
          accountId # string,
          name # string,
          address # string,
          city # string,
          country # string,
          nedermanId # string,
          supplierName # string,
          groupName # string,
          applications {
            applicationId # string,
            name # string,
            roles {
                roleId # string,
                name # string,
                description # string,
                companyType # string,
                customerAdmin # string
              },
            instances {
                instanceId # string,
                name # string
              },
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              }
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public createCustomer(req: CustomerCompanyCreateCommand): Observable<CustomerCompanyCreateResult> {
    return this.graphql.mutate<Result<CustomerCompanyCreateResult>>({
      mutation: gql`mutation request($req: CustomerCompanyCreateCommand!) {
        result: CustomerAccountApi_createCustomer(req: $req) {
          company {
            customerId # string,
            name # string,
            address # string,
            city # string,
            zipCode # string,
            state # string,
            country # string,
            telephone # string,
            fax # string,
            language # string,
            currency # string,
            vat # string,
            storeNumber # string,
            segment # string,
            created # Date,
            updated # Date,
            version # number,
            # users # CustomerUser[] ...,
            # suppliers # Supplier[] ...,
            # tenantGroup # TenantGroup ...,
            userCount # number,
            associated # boolean
          },
          group {
            group {
                tenantGroupId # string,
                tenantParentId # string,
                name # string,
                type # string,
                level # number,
                # tenants # Tenant[] ...
              },
            proxy {
                tenantGroupId # string,
                tenantParentId # string,
                name # string,
                type # string,
                level # number,
                # tenants # Tenant[] ...
              }
          },
          tenant {
            tenantId # string,
            applicationId # string,
            instanceId # string,
            tenantGroupId # string,
            serviceLevel # string,
            status # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # instance # Instance ...,
            # tenantGroup # TenantGroup ...,
            # parameters # TenantParameter[] ...,
            associated # boolean
          },
          supplier {
            supplierId # string,
            applicationId # string,
            customerId # string,
            nedermanId # string,
            accountNumber # string,
            status # Status,
            # nedermanCompany # Nederman ...,
            version # number,
            created # Date,
            upldated # Date,
            associated # boolean
          }
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class CustomerCompanyApi {
  constructor(private graphql: Apollo) { }

  public search(req: CustomerCompanyQuery, refresh?: boolean): Observable<CustomerCompanyList> {
    return this.graphql.query<Result<CustomerCompanyList>>({
      query: gql`query request($req: CustomerCompanyQuery!) {
        result: CustomerCompanyApi_search(req: $req) {
          query # any,
          items {
            customerId # string,
            name # string,
            tenantGroupId # string,
            tenantGroupName # string,
            country # string,
            vat # string,
            applicationCount # number,
            userCount # number,
            version # number,
            created # Date,
            updated # Date
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public overview(req: CustomerCompanyRequest, refresh?: boolean): Observable<CustomerCompanyOverview> {
    return this.graphql.query<Result<CustomerCompanyOverview>>({
      query: gql`query request($req: CustomerCompanyRequest!) {
        result: CustomerCompanyApi_overview(req: $req) {
          customerId # string,
          name # string,
          address # string,
          city # string,
          zipCode # string,
          state # string,
          country # string,
          telephone # string,
          fax # string,
          language # string,
          currency # string,
          created # Date,
          updated # Date,
          version # number,
          tenantGroup {
            tenantGroupId # string,
            tenantParentId # string,
            name # string,
            type # string,
            level # number,
            tenants {
                tenantId # string,
                applicationId # string,
                instanceId # string,
                tenantGroupId # string,
                serviceLevel # string,
                status # string,
                created # Date,
                updated # Date,
                version # number,
                # application # Application ...,
                instance {
                      instanceId # string,
                      applicationId # string,
                      parentId # string,
                      name # string,
                      type # string,
                      nedermanTenant # string,
                      customerAdmin # string,
                      url # string,
                      dataCenter # string,
                      directoryId # string,
                      clientId # string,
                      enterpriseId # string,
                      accessObjectId # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # application # Application ...,
                      # directory # Directory ...,
                      # tenants # Tenant[] ...,
                      # nedermanGrants # NedermanGrant[] ...,
                      # customerGrants # CustomerGrant[] ...,
                      customerCompanyCount # number,
                      customerUserCount # number,
                      nedermanUserCount # number
                    },
                # tenantGroup # TenantGroup ...,
                parameters {
                      tenantId # string,
                      parameterId # string,
                      value # string,
                      definition {
                              parameterId # string,
                              applicationId # string,
                              name # string,
                              label # string,
                              description # string,
                              level # string,
                              ordinal # number,
                              status # string,
                              parentId # string,
                              type # string,
                              options # string,
                              minValue # number,
                              maxValue # number,
                              maxLength # number,
                              defaultValue # string,
                              expression # string,
                              created # Date,
                              updated # Date,
                              version # number,
                              # parent # ApplicationParameter ...,
                              # children # ApplicationParameter[] ...,
                              # application # Application ...,
                              # tenantParameters # TenantParameter[] ...,
                              # customerParameters # CustomerParameter[] ...,
                              # nedermanParameters # NedermanParameter[] ...,
                              value # string
                            },
                      # tenant # Tenant ...
                    },
                associated # boolean
              }
          },
          suppliers {
            supplierId # string,
            applicationId # string,
            customerId # string,
            nedermanId # string,
            accountNumber # string,
            status # Status,
            nedermanCompany {
                nedermanId # string,
                erpDataId # string,
                name # string,
                address # string,
                city # string,
                zipCode # string,
                state # string,
                country # string,
                telephone # string,
                fax # string,
                language # string,
                currency # string,
                vatNo # string,
                mailboxGLN # string,
                companyGLN # string,
                deleteFromSource # string,
                lastModified # string,
                created # Date,
                updated # Date,
                version # number,
                # users # NedermanUser[] ...,
                # accounts # CustomerAccount[] ...,
                # suppliers # Supplier[] ...,
                # grants # NedermanGrant[] ...,
                # tenantGroup # TenantGroup ...,
                userCount # number,
                invitable # boolean
              },
            version # number,
            created # Date,
            upldated # Date,
            associated # boolean
          },
          associated # boolean
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public edit(req: CustomerCompanyEditInput): Observable<CustomerCompanyOverview> {
    return this.graphql.mutate<Result<CustomerCompanyOverview>>({
      mutation: gql`mutation request($req: CustomerCompanyEditInput!) {
        result: CustomerCompanyApi_edit(req: $req) {
          customerId # string,
          name # string,
          address # string,
          city # string,
          zipCode # string,
          state # string,
          country # string,
          telephone # string,
          fax # string,
          language # string,
          currency # string,
          created # Date,
          updated # Date,
          version # number,
          tenantGroup {
            tenantGroupId # string,
            tenantParentId # string,
            name # string,
            type # string,
            level # number,
            # tenants # Tenant[] ...
          },
          suppliers {
            supplierId # string,
            applicationId # string,
            customerId # string,
            nedermanId # string,
            accountNumber # string,
            status # Status,
            # nedermanCompany # Nederman ...,
            version # number,
            created # Date,
            upldated # Date,
            associated # boolean
          },
          associated # boolean
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public prepareAddSupplier(req: AddSupplierPrepareCreate, refresh?: boolean): Observable<AddSupplierPrepareAdvice> {
    return this.graphql.query<Result<AddSupplierPrepareAdvice>>({
      query: gql`query request($req: AddSupplierPrepareCreate!) {
        result: CustomerCompanyApi_prepareAddSupplier(req: $req) {
          customerId # string,
          accountId # string,
          name # string,
          supplierName # string,
          nedermanId # string,
          accountNumber # string,
          applications {
            applicationId # string,
            name # string,
            # roles # ApplicationRoleListItem[] ...,
            instances {
                instanceId # string,
                name # string
              },
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              }
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public addSupplier(req: AddSupplierCommand): Observable<AddSupplierResult> {
    return this.graphql.mutate<Result<AddSupplierResult>>({
      mutation: gql`mutation request($req: AddSupplierCommand!) {
        result: CustomerCompanyApi_addSupplier(req: $req) {
          tenant {
            tenantId # string,
            applicationId # string,
            instanceId # string,
            tenantGroupId # string,
            serviceLevel # string,
            status # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # instance # Instance ...,
            # tenantGroup # TenantGroup ...,
            # parameters # TenantParameter[] ...,
            associated # boolean
          },
          supplier {
            supplierId # string,
            applicationId # string,
            customerId # string,
            nedermanId # string,
            accountNumber # string,
            status # Status,
            # nedermanCompany # Nederman ...,
            version # number,
            created # Date,
            upldated # Date,
            associated # boolean
          }
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class CustomerUserApi {
  constructor(private graphql: Apollo) { }

  public search(req: CustomerUserQueryInput, refresh?: boolean): Observable<CustomerUserList> {
    return this.graphql.query<Result<CustomerUserList>>({
      query: gql`query request($req: CustomerUserQueryInput!) {
        result: CustomerUserApi_search(req: $req) {
          query # any,
          items {
            userId # string,
            email # string,
            name # string,
            customerId # string,
            customerName # string,
            applicationCount # number,
            version # number,
            created # Date,
            updated # Date
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public overview(req: CustomerUserRequest, refresh?: boolean): Observable<CustomerUserOverview> {
    return this.graphql.query<Result<CustomerUserOverview>>({
      query: gql`query request($req: CustomerUserRequest!) {
        result: CustomerUserApi_overview(req: $req) {
          userId # string,
          customerId # string,
          email # string,
          displayName # string,
          firstName # string,
          lastName # string,
          address # string,
          city # string,
          country # string,
          telephone # string,
          invited # Date,
          inviter # string,
          created # Date,
          updated # Date,
          version # number,
          customer {
            customerId # string,
            name # string,
            address # string,
            city # string,
            zipCode # string,
            state # string,
            country # string,
            telephone # string,
            fax # string,
            language # string,
            currency # string,
            vat # string,
            storeNumber # string,
            segment # string,
            created # Date,
            updated # Date,
            version # number,
            # users # CustomerUser[] ...,
            # suppliers # Supplier[] ...,
            # tenantGroup # TenantGroup ...,
            userCount # number,
            associated # boolean
          },
          grants {
            grantId # string,
            userId # string,
            applicationId # string,
            instanceId # string,
            roleId # string,
            roleName # string,
            status # string,
            created # Date,
            updated # Date,
            version # number,
            instance {
                instanceId # string,
                applicationId # string,
                parentId # string,
                name # string,
                type # string,
                nedermanTenant # string,
                customerAdmin # string,
                url # string,
                dataCenter # string,
                directoryId # string,
                clientId # string,
                enterpriseId # string,
                accessObjectId # string,
                created # Date,
                updated # Date,
                version # number,
                # application # Application ...,
                # directory # Directory ...,
                # tenants # Tenant[] ...,
                # nedermanGrants # NedermanGrant[] ...,
                # customerGrants # CustomerGrant[] ...,
                customerCompanyCount # number,
                customerUserCount # number,
                nedermanUserCount # number
              },
            role {
                roleId # string,
                applicationId # string,
                name # string,
                description # string,
                companyType # string,
                userType # string,
                customerAdmin # string,
                precedance # number,
                groupName # string,
                groupObjectId # string,
                created # Date,
                updated # Date,
                version # number,
                # application # Application ...,
                # nedermanGrants # NedermanGrant[] ...,
                # customerGrants # CustomerGrant[] ...
              },
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              },
            editable # boolean
          },
          editable # boolean
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public edit(req: CustomerUserEditInput): Observable<CustomerUserOverview> {
    return this.graphql.mutate<Result<CustomerUserOverview>>({
      mutation: gql`mutation request($req: CustomerUserEditInput!) {
        result: CustomerUserApi_edit(req: $req) {
          userId # string,
          customerId # string,
          email # string,
          displayName # string,
          firstName # string,
          lastName # string,
          address # string,
          city # string,
          country # string,
          telephone # string,
          invited # Date,
          inviter # string,
          created # Date,
          updated # Date,
          version # number,
          customer {
            customerId # string,
            name # string,
            address # string,
            city # string,
            zipCode # string,
            state # string,
            country # string,
            telephone # string,
            fax # string,
            language # string,
            currency # string,
            vat # string,
            storeNumber # string,
            segment # string,
            created # Date,
            updated # Date,
            version # number,
            # users # CustomerUser[] ...,
            # suppliers # Supplier[] ...,
            # tenantGroup # TenantGroup ...,
            userCount # number,
            associated # boolean
          },
          grants {
            grantId # string,
            userId # string,
            applicationId # string,
            instanceId # string,
            roleId # string,
            roleName # string,
            status # string,
            created # Date,
            updated # Date,
            version # number,
            # instance # Instance ...,
            # role # ApplicationRole ...,
            # parameters # ParameterInstance[] ...,
            editable # boolean
          },
          editable # boolean
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public prepareInviteUser(req: InviteUserPrepare, refresh?: boolean): Observable<InviteUserAdvice> {
    return this.graphql.query<Result<InviteUserAdvice>>({
      query: gql`query request($req: InviteUserPrepare!) {
        result: CustomerUserApi_prepareInviteUser(req: $req) {
          companyName # string,
          customerId # string,
          applications {
            applicationId # string,
            name # string,
            roles {
                roleId # string,
                name # string,
                description # string,
                companyType # string,
                customerAdmin # string
              },
            instances {
                instanceId # string,
                name # string
              },
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              }
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public inviteUser(req: InviteUserCommand): Observable<InviteUserResult> {
    return this.graphql.mutate<Result<InviteUserResult>>({
      mutation: gql`mutation request($req: InviteUserCommand!) {
        result: CustomerUserApi_inviteUser(req: $req) {
          customerId # string,
          userId # string,
          email # string,
          applicationId # string,
          instanceId # string,
          roleId # string
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public prepareAssignCustomerGrant(req: CustomerUserRequest, refresh?: boolean): Observable<CustomerGrantPrepareAssignAdvice> {
    return this.graphql.query<Result<CustomerGrantPrepareAssignAdvice>>({
      query: gql`query request($req: CustomerUserRequest!) {
        result: CustomerUserApi_prepareAssignCustomerGrant(req: $req) {
          userId # string,
          email # string,
          displayName # string,
          companyName # string,
          applications {
            applicationId # string,
            name # string,
            roles {
                roleId # string,
                name # string,
                description # string,
                companyType # string,
                customerAdmin # string
              },
            instances {
                instanceId # string,
                name # string
              },
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              }
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public assignCustomerGrant(req: CustomerGrantAssignCommand): Observable<CustomerUserGrant> {
    return this.graphql.mutate<Result<CustomerUserGrant>>({
      mutation: gql`mutation request($req: CustomerGrantAssignCommand!) {
        result: CustomerUserApi_assignCustomerGrant(req: $req) {
          grantId # string,
          userId # string,
          applicationId # string,
          instanceId # string,
          roleId # string,
          roleName # string,
          status # string,
          created # Date,
          updated # Date,
          version # number,
          instance {
            instanceId # string,
            applicationId # string,
            parentId # string,
            name # string,
            type # string,
            nedermanTenant # string,
            customerAdmin # string,
            url # string,
            dataCenter # string,
            directoryId # string,
            clientId # string,
            enterpriseId # string,
            accessObjectId # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # directory # Directory ...,
            # tenants # Tenant[] ...,
            # nedermanGrants # NedermanGrant[] ...,
            # customerGrants # CustomerGrant[] ...,
            customerCompanyCount # number,
            customerUserCount # number,
            nedermanUserCount # number
          },
          role {
            roleId # string,
            applicationId # string,
            name # string,
            description # string,
            companyType # string,
            userType # string,
            customerAdmin # string,
            precedance # number,
            groupName # string,
            groupObjectId # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # nedermanGrants # NedermanGrant[] ...,
            # customerGrants # CustomerGrant[] ...
          },
          # parameters # ParameterInstance[] ...,
          editable # boolean
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public editGrant(req: CustomerGrantEditCommand): Observable<CustomerUserGrant> {
    return this.graphql.mutate<Result<CustomerUserGrant>>({
      mutation: gql`mutation request($req: CustomerGrantEditCommand!) {
        result: CustomerUserApi_editGrant(req: $req) {
          grantId # string,
          userId # string,
          applicationId # string,
          instanceId # string,
          roleId # string,
          roleName # string,
          status # string,
          created # Date,
          updated # Date,
          version # number,
          instance {
            instanceId # string,
            applicationId # string,
            parentId # string,
            name # string,
            type # string,
            nedermanTenant # string,
            customerAdmin # string,
            url # string,
            dataCenter # string,
            directoryId # string,
            clientId # string,
            enterpriseId # string,
            accessObjectId # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # directory # Directory ...,
            # tenants # Tenant[] ...,
            # nedermanGrants # NedermanGrant[] ...,
            # customerGrants # CustomerGrant[] ...,
            customerCompanyCount # number,
            customerUserCount # number,
            nedermanUserCount # number
          },
          role {
            roleId # string,
            applicationId # string,
            name # string,
            description # string,
            companyType # string,
            userType # string,
            customerAdmin # string,
            precedance # number,
            groupName # string,
            groupObjectId # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # nedermanGrants # NedermanGrant[] ...,
            # customerGrants # CustomerGrant[] ...
          },
          parameters {
            parameterId # string,
            value # any,
            # definition # ApplicationParameter ...
          },
          editable # boolean
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public deleteUser(req: CustomerUserRequest): Observable<CustomerUserOverview> {
    return this.graphql.mutate<Result<CustomerUserOverview>>({
      mutation: gql`mutation request($req: CustomerUserRequest!) {
        result: CustomerUserApi_deleteCustomerUser(req: $req) {
          userId # string,
          email # string,
          customerId #string,
          displayName # string,
          firstName # string,
          lastName # string,
          invited # date
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class LoginApi {
  constructor(private graphql: Apollo) { }

  public fetch(sessionId: string, refresh?: boolean): Observable<SessionInfo> {
    return this.graphql.query<Result<SessionInfo>>({
      query: gql`query request($sessionId: String!) {
        result: LoginApi_fetch(sessionId: $sessionId) {
          sessionId # string,
          issuer # string,
          audience # string,
          subject # string,
          remote # boolean,
          userId # string,
          role # string,
          scope # string,
          companyId # string,
          email # string,
          name # string,
          ipAddress # string,
          previousLogin # Date,
          issued # Date,
          expires # Date,
          token # string
        }
      }`,
      variables: { sessionId },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public discard(sessionId: string): Observable<SessionInfo> {
    return this.graphql.mutate<Result<SessionInfo>>({
      mutation: gql`mutation request($sessionId: String!) {
        result: LoginApi_discard(sessionId: $sessionId) {
          sessionId # string,
          issuer # string,
          audience # string,
          subject # string,
          remote # boolean,
          userId # string,
          role # string,
          scope # string,
          companyId # string,
          email # string,
          name # string,
          ipAddress # string,
          previousLogin # Date,
          issued # Date,
          expires # Date,
          token # string
        }
      }`,
      variables: { sessionId }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public reissue(grantId: string, refresh?: boolean): Observable<SessionInfo> {
    return this.graphql.query<Result<SessionInfo>>({
      query: gql`query request($grantId: String!) {
        result: LoginApi_reissue(grantId: $grantId) {
          sessionId # string,
          issuer # string,
          audience # string,
          subject # string,
          remote # boolean,
          userId # string,
          role # string,
          scope # string,
          companyId # string,
          email # string,
          name # string,
          ipAddress # string,
          previousLogin # Date,
          issued # Date,
          expires # Date,
          token # string
        }
      }`,
      variables: { grantId },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public grants(refresh?: boolean): Observable<LoginGrantList> {
    return this.graphql.query<Result<LoginGrantList>>({
      query: gql`query request {
        result: LoginApi_grants {
          userId # string,
          grants {
            grantId # string,
            nedermanId # string,
            roleId # string,
            roleName # string,
            label # string,
            selected # boolean
          }
        }
      }`,
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class NedermanCompanyApi {
  constructor(private graphql: Apollo) { }

  public search(req: NedermanCompanyQueryInput, refresh?: boolean): Observable<NedermanCompanyList> {
    return this.graphql.query<Result<NedermanCompanyList>>({
      query: gql`query request($req: NedermanCompanyQueryInput!) {
        result: NedermanCompanyApi_search(req: $req) {
          query {
            search # string,
            orderBy # string,
            offset # number
          },
          items {
            nedermanId # string,
            erpDataId # string,
            name # string,
            city # string,
            country # string,
            userCount # number,
            version # number,
            created # Date,
            updated # Date
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public overview(req: NedermanCompanyRequest, refresh?: boolean): Observable<Nederman> {
    return this.graphql.query<Result<Nederman>>({
      query: gql`query request($req: NedermanCompanyRequest!) {
        result: NedermanCompanyApi_overview(req: $req) {
          nedermanId # string,
          erpDataId # string,
          name # string,
          address # string,
          city # string,
          zipCode # string,
          state # string,
          country # string,
          telephone # string,
          fax # string,
          language # string,
          currency # string,
          vatNo # string,
          mailboxGLN # string,
          companyGLN # string,
          deleteFromSource # string,
          lastModified # string,
          created # Date,
          updated # Date,
          version # number,
          # users # NedermanUser[] ...,
          # accounts # CustomerAccount[] ...,
          # suppliers # Supplier[] ...,
          # grants # NedermanGrant[] ...,
          # tenantGroup # TenantGroup ...,
          userCount # number,
          invitable # boolean
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public listSupplierCustomers(req: ListSupplierCustomersRequest, refresh?: boolean): Observable<SupplierCustomerCompaniesList> {
    return this.graphql.query<Result<SupplierCustomerCompaniesList>>({
      query: gql`query request($req: ListSupplierCustomersRequest!) {
        result: NedermanCompanyApi_listSupplierCustomers(req: $req) {
          query # any,
          items {
            customerId # string,
            name # string,
            tenantGroupId # string,
            tenantGroupName # string,
            country # string,
            vat # string,
            applicationCount # number,
            userCount # number,
            version # number,
            created # Date,
            updated # Date
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class NedermanUserApi {
  constructor(private graphql: Apollo) { }

  public search(req: NedermanUserSearchInput, refresh?: boolean): Observable<NedermanSearchResult> {
    return this.graphql.query<Result<NedermanSearchResult>>({
      query: gql`query request($req: NedermanUserSearchInput!) {
        result: NedermanUserApi_search(req: $req) {
          query # any,
          items {
            userId # string,
            email # string,
            name # string,
            nedermanId # string,
            nedermanName # string,
            country # string,
            applicationCount # number,
            invited # Date,
            version # number,
            created # Date,
            updated # Date
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public overview(req: NedermanUserRequest, refresh?: boolean): Observable<NedermanUserOverview> {
    return this.graphql.query<Result<NedermanUserOverview>>({
      query: gql`query request($req: NedermanUserRequest!) {
        result: NedermanUserApi_overview(req: $req) {
          userId # string,
          nedermanId # string,
          email # string,
          firstName # string,
          lastName # string,
          displayName # string,
          jobTitle # string,
          dirCompany # string,
          nedermanCompany # string,
          department # string,
          office # string,
          street # string,
          poBox # string,
          postalCode # string,
          city # string,
          stateProvince # string,
          country # string,
          mobilePhoneNumber # string,
          telephoneNumber # string,
          manager # string,
          userStatus # string,
          samAccountName # string,
          GUID # string,
          SID # string,
          creationDate # string,
          lastModified # string,
          firstLogin # Date,
          lastLogin # Date,
          assigns # number,
          invited # Date,
          inviter # string,
          created # Date,
          updated # Date,
          version # number,
          azureAdObjectId # string,
          erpDataId # string,
          siteId # string,
          editable # boolean,
          grants {
            grantId # string,
            userId # string,
            applicationId # string,
            instanceId # string,
            instance {
                instanceId # string,
                applicationId # string,
                parentId # string,
                name # string,
                type # string,
                nedermanTenant # string,
                customerAdmin # string,
                url # string,
                dataCenter # string,
                directoryId # string,
                clientId # string,
                enterpriseId # string,
                accessObjectId # string,
                created # Date,
                updated # Date,
                version # number,
                # application # Application ...,
                # directory # Directory ...,
                # tenants # Tenant[] ...,
                # nedermanGrants # NedermanGrant[] ...,
                # customerGrants # CustomerGrant[] ...,
                customerCompanyCount # number,
                customerUserCount # number,
                nedermanUserCount # number
              },
            nedermanId # string,
            roleId # string,
            roleName # string,
            status # string,
            nedermanCompany {
                nedermanId # string,
                erpDataId # string,
                name # string,
                address # string,
                city # string,
                zipCode # string,
                state # string,
                country # string,
                telephone # string,
                fax # string,
                language # string,
                currency # string,
                vatNo # string,
                mailboxGLN # string,
                companyGLN # string,
                deleteFromSource # string,
                lastModified # string,
                created # Date,
                updated # Date,
                version # number,
                # users # NedermanUser[] ...,
                # accounts # CustomerAccount[] ...,
                # suppliers # Supplier[] ...,
                # grants # NedermanGrant[] ...,
                # tenantGroup # TenantGroup ...,
                userCount # number,
                invitable # boolean
              },
            role {
                roleId # string,
                applicationId # string,
                name # string,
                description # string,
                companyType # string,
                userType # string,
                customerAdmin # string,
                precedance # number,
                groupName # string,
                groupObjectId # string,
                created # Date,
                updated # Date,
                version # number,
                # application # Application ...,
                # nedermanGrants # NedermanGrant[] ...,
                # customerGrants # CustomerGrant[] ...
              },
            version # number,
            created # Date,
            updated # Date,
            editable # boolean,
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              }
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public edit(req: NedermanUserEditInput): Observable<NedermanUserOverview> {
    return this.graphql.mutate<Result<NedermanUserOverview>>({
      mutation: gql`mutation request($req: NedermanUserEditInput!) {
        result: NedermanUserApi_edit(req: $req) {
          userId # string,
          nedermanId # string,
          email # string,
          firstName # string,
          lastName # string,
          displayName # string,
          jobTitle # string,
          dirCompany # string,
          nedermanCompany # string,
          department # string,
          office # string,
          street # string,
          poBox # string,
          postalCode # string,
          city # string,
          stateProvince # string,
          country # string,
          mobilePhoneNumber # string,
          telephoneNumber # string,
          manager # string,
          userStatus # string,
          samAccountName # string,
          GUID # string,
          SID # string,
          creationDate # string,
          lastModified # string,
          firstLogin # Date,
          lastLogin # Date,
          assigns # number,
          invited # Date,
          inviter # string,
          created # Date,
          updated # Date,
          version # number,
          azureAdObjectId # string,
          erpDataId # string,
          siteId # string,
          editable # boolean,
          grants {
            grantId # string,
            userId # string,
            applicationId # string,
            instanceId # string,
            # instance # Instance ...,
            nedermanId # string,
            roleId # string,
            roleName # string,
            status # string,
            nedermanCompany {
                nedermanId # string,
                erpDataId # string,
                name # string,
                address # string,
                city # string,
                zipCode # string,
                state # string,
                country # string,
                telephone # string,
                fax # string,
                language # string,
                currency # string,
                vatNo # string,
                mailboxGLN # string,
                companyGLN # string,
                deleteFromSource # string,
                lastModified # string,
                created # Date,
                updated # Date,
                version # number,
                # users # NedermanUser[] ...,
                # accounts # CustomerAccount[] ...,
                # suppliers # Supplier[] ...,
                # grants # NedermanGrant[] ...,
                # tenantGroup # TenantGroup ...,
                userCount # number,
                invitable # boolean
              },
            # role # ApplicationRole ...,
            version # number,
            created # Date,
            updated # Date,
            editable # boolean,
            # parameters # ParameterInstance[] ...
          }
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public listCompanies(req: NedermanListCompaniesRequest, refresh?: boolean): Observable<NedermanAssignedCompaniesList> {
    return this.graphql.query<Result<NedermanAssignedCompaniesList>>({
      query: gql`query request($req: NedermanListCompaniesRequest!) {
        result: NedermanUserApi_listCompanies(req: $req) {
          items {
            nedermanId # string,
            nedermanCompany # string,
            # applications # ApplicationAdvice[] ...
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public prepareInviteUser(req: NedermanUserInvitePrepare, refresh?: boolean): Observable<NedermanUserInviteAdvice> {
    return this.graphql.query<Result<NedermanUserInviteAdvice>>({
      query: gql`query request($req: NedermanUserInvitePrepare!) {
        result: NedermanUserApi_prepareInviteUser(req: $req) {
          companyName # string,
          nedermanId # string,
          applications {
            applicationId # string,
            name # string,
            roles {
                roleId # string,
                name # string,
                description # string,
                companyType # string,
                customerAdmin # string
              },
            instances {
                instanceId # string,
                name # string
              },
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              }
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public inviteUser(req: NedermanUserInviteCommand): Observable<NedermanUserInviteResult> {
    return this.graphql.mutate<Result<NedermanUserInviteResult>>({
      mutation: gql`mutation request($req: NedermanUserInviteCommand!) {
        result: NedermanUserApi_inviteUser(req: $req) {
          nedermanId # string,
          userId # string,
          email # string,
          applicationId # string,
          instanceId # string,
          roleId # string
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public prepareAssignGrant(req: NedermanUserRequest, refresh?: boolean): Observable<NedermanAssignGrantAdvice> {
    return this.graphql.query<Result<NedermanAssignGrantAdvice>>({
      query: gql`query request($req: NedermanUserRequest!) {
        result: NedermanUserApi_prepareAssignGrant(req: $req) {
          userId # string,
          nedermanId # string,
          firstName # string,
          lastName # string,
          nedermanCompany # string,
          nedermanCompanies {
            nedermanId # string,
            nedermanCompany # string,
            applications {
                applicationId # string,
                name # string,
                roles {
                      roleId # string,
                      name # string,
                      description # string,
                      companyType # string,
                      customerAdmin # string
                    },
                instances {
                      instanceId # string,
                      name # string
                    },
                parameters {
                      parameterId # string,
                      value # any,
                      definition {
                              parameterId # string,
                              applicationId # string,
                              name # string,
                              label # string,
                              description # string,
                              level # string,
                              ordinal # number,
                              status # string,
                              parentId # string,
                              type # string,
                              options # string,
                              minValue # number,
                              maxValue # number,
                              maxLength # number,
                              defaultValue # string,
                              expression # string,
                              created # Date,
                              updated # Date,
                              version # number,
                              # parent # ApplicationParameter ...,
                              # children # ApplicationParameter[] ...,
                              # application # Application ...,
                              # tenantParameters # TenantParameter[] ...,
                              # customerParameters # CustomerParameter[] ...,
                              # nedermanParameters # NedermanParameter[] ...,
                              value # string
                            }
                    }
              }
          },
          applications {
            applicationId # string,
            name # string,
            roles {
                roleId # string,
                name # string,
                description # string,
                companyType # string,
                customerAdmin # string
              },
            instances {
                instanceId # string,
                name # string
              },
            parameters {
                parameterId # string,
                value # any,
                definition {
                      parameterId # string,
                      applicationId # string,
                      name # string,
                      label # string,
                      description # string,
                      level # string,
                      ordinal # number,
                      status # string,
                      parentId # string,
                      type # string,
                      options # string,
                      minValue # number,
                      maxValue # number,
                      maxLength # number,
                      defaultValue # string,
                      expression # string,
                      created # Date,
                      updated # Date,
                      version # number,
                      # parent # ApplicationParameter ...,
                      # children # ApplicationParameter[] ...,
                      # application # Application ...,
                      # tenantParameters # TenantParameter[] ...,
                      # customerParameters # CustomerParameter[] ...,
                      # nedermanParameters # NedermanParameter[] ...,
                      value # string
                    }
              }
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public assignGrant(req: NedermanAssignGrantCommand): Observable<NedermanGrantItem> {
    return this.graphql.mutate<Result<NedermanGrantItem>>({
      mutation: gql`mutation request($req: NedermanAssignGrantCommand!) {
        result: NedermanUserApi_assignGrant(req: $req) {
          grantId # string,
          userId # string,
          applicationId # string,
          instanceId # string,
          instance {
            instanceId # string,
            applicationId # string,
            parentId # string,
            name # string,
            type # string,
            nedermanTenant # string,
            customerAdmin # string,
            url # string,
            dataCenter # string,
            directoryId # string,
            clientId # string,
            enterpriseId # string,
            accessObjectId # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # directory # Directory ...,
            # tenants # Tenant[] ...,
            # nedermanGrants # NedermanGrant[] ...,
            # customerGrants # CustomerGrant[] ...,
            customerCompanyCount # number,
            customerUserCount # number,
            nedermanUserCount # number
          },
          nedermanId # string,
          roleId # string,
          roleName # string,
          status # string,
          nedermanCompany {
            nedermanId # string,
            erpDataId # string,
            name # string,
            address # string,
            city # string,
            zipCode # string,
            state # string,
            country # string,
            telephone # string,
            fax # string,
            language # string,
            currency # string,
            vatNo # string,
            mailboxGLN # string,
            companyGLN # string,
            deleteFromSource # string,
            lastModified # string,
            created # Date,
            updated # Date,
            version # number,
            # users # NedermanUser[] ...,
            # accounts # CustomerAccount[] ...,
            # suppliers # Supplier[] ...,
            # grants # NedermanGrant[] ...,
            # tenantGroup # TenantGroup ...,
            userCount # number,
            invitable # boolean
          },
          # role # ApplicationRole ...,
          version # number,
          created # Date,
          updated # Date,
          editable # boolean,
          parameters {
            parameterId # string,
            value # any,
            # definition # ApplicationParameter ...
          }
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  public editGrant(req: NedermanEditGrantCommand): Observable<NedermanGrantItem> {
    return this.graphql.mutate<Result<NedermanGrantItem>>({
      mutation: gql`mutation request($req: NedermanEditGrantCommand!) {
        result: NedermanUserApi_editGrant(req: $req) {
          grantId # string,
          userId # string,
          applicationId # string,
          instanceId # string,
          instance {
            instanceId # string,
            applicationId # string,
            parentId # string,
            name # string,
            type # string,
            nedermanTenant # string,
            customerAdmin # string,
            url # string,
            dataCenter # string,
            directoryId # string,
            clientId # string,
            enterpriseId # string,
            accessObjectId # string,
            created # Date,
            updated # Date,
            version # number,
            # application # Application ...,
            # directory # Directory ...,
            # tenants # Tenant[] ...,
            # nedermanGrants # NedermanGrant[] ...,
            # customerGrants # CustomerGrant[] ...,
            customerCompanyCount # number,
            customerUserCount # number,
            nedermanUserCount # number
          },
          nedermanId # string,
          roleId # string,
          roleName # string,
          status # string,
          nedermanCompany {
            nedermanId # string,
            erpDataId # string,
            name # string,
            address # string,
            city # string,
            zipCode # string,
            state # string,
            country # string,
            telephone # string,
            fax # string,
            language # string,
            currency # string,
            vatNo # string,
            mailboxGLN # string,
            companyGLN # string,
            deleteFromSource # string,
            lastModified # string,
            created # Date,
            updated # Date,
            version # number,
            # users # NedermanUser[] ...,
            # accounts # CustomerAccount[] ...,
            # suppliers # Supplier[] ...,
            # grants # NedermanGrant[] ...,
            # tenantGroup # TenantGroup ...,
            userCount # number,
            invitable # boolean
          },
          # role # ApplicationRole ...,
          version # number,
          created # Date,
          updated # Date,
          editable # boolean,
          parameters {
            parameterId # string,
            value # any,
            # definition # ApplicationParameter ...
          }
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }

  // public deleteUser(req: NedermanUserRequest): Observable<NedermanUserInfo> {
  //   return this.graphql.mutate<Result<NedermanUserInfo>>({
  //     mutation: gql`mutation request($req: NedermanUserRequest!) {
  //       result: NedermanUserApi_deleteNedermanUser(req: $req) {
  //         userId # string,
  //         email # string,
  //         nedermanId #string,
  //         nedermanName # string,
  //         name # string,
  //         invited # date
  //       }
  //     }`,
  //     variables: { req }
  //   }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  // }
}

@Injectable()
export class SupplierApi {
  constructor(private graphql: Apollo) { }

  public edit(request: EditSupplierCommand): Observable<SupplierItem> {
    return this.graphql.mutate<Result<SupplierItem>>({
      mutation: gql`mutation request($request: EditSupplierCommand!) {
        result: SupplierApi_edit(request: $request) {
          supplierId # string,
          applicationId # string,
          customerId # string,
          nedermanId # string,
          accountNumber # string,
          status # Status,
          nedermanCompany {
            nedermanId # string,
            erpDataId # string,
            name # string,
            address # string,
            city # string,
            zipCode # string,
            state # string,
            country # string,
            telephone # string,
            fax # string,
            language # string,
            currency # string,
            vatNo # string,
            mailboxGLN # string,
            companyGLN # string,
            deleteFromSource # string,
            lastModified # string,
            created # Date,
            updated # Date,
            version # number,
            # users # NedermanUser[] ...,
            # accounts # CustomerAccount[] ...,
            # suppliers # Supplier[] ...,
            # grants # NedermanGrant[] ...,
            # tenantGroup # TenantGroup ...,
            userCount # number,
            invitable # boolean
          },
          version # number,
          created # Date,
          upldated # Date,
          associated # boolean
        }
      }`,
      variables: { request }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class SyncApi {
  constructor(private graphql: Apollo) { }

  public search(req: SyncQueryInput, refresh?: boolean): Observable<SyncList> {
    return this.graphql.query<Result<SyncList>>({
      query: gql`query request($req: SyncQueryInput!) {
        result: SyncApi_search(req: $req) {
          query # any,
          items {
            syncId # string,
            type # string,
            lineCount # number,
            discardCount # number,
            batchCount # number,
            status # string,
            created # Date,
            # batch # BatchItem[] ...
          }
        }
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }

  public csvReport(req: SyncQueryInput, refresh?: boolean): Observable<any> {
    return this.graphql.query<Result<any>>({
      query: gql`query request($req: SyncQueryInput!) {
        result: SyncApi_csvReport(req: $req) # : ANY
      }`,
      variables: { req },
      fetchPolicy: NO_CACHE ? 'no-cache' : refresh ? 'network-only' : 'cache-first'
    }).pipe(map(res => result(res)), catchError(err => erorr(err)));
  }
}

@Injectable()
export class TenantApi {
  constructor(private graphql: Apollo) { }

  public edit(req: EditTenantCommand): Observable<TenantCreateResult> {
    return this.graphql.mutate<Result<TenantCreateResult>>({
      mutation: gql`mutation request($req: EditTenantCommand!) {
        result: TenantApi_edit(req: $req) {
          tenantId # string,
          applicationId # string,
          instanceId # string,
          tenantGroupId # string,
          serviceLevel # string,
          status # Status
        }
      }`,
      variables: { req }
    }).pipe(map(res => fetch(res)), catchError(err => erorr(err)));
  }
}

///////// ENUM ////////
export enum ApplicationOrderyBy {
  Name = 'Name'
}

export enum CompanyType {
  Access = 'Access',
  Holding = 'Holding',
  Nederman = 'Nederman',
  Customer = 'Customer',
  Partner = 'Partner'
}

export enum GroupType {
  Proxy = 'Proxy',
  Group = 'Group'
}

export enum InstanceType {
  Public = 'Public',
  Internal = 'Internal',
  External = 'External',
  Disabled = 'Disabled'
}

export enum NedermanCompanyOrderBy {
  Name = 'Name',
  Counry = 'Counry',
  City = 'City',
  NedermanId = 'NedermanId'
}

export enum NedermanUserSearchAssign {
  Unassigned = 'Unassigned',
  Assigned = 'Assigned',
  All = 'All'
}

export enum NedermanUserSearchOrderBy {
  Name = 'Name',
  Email = 'Email',
  Company = 'Company'
}

export enum NedermanUserSearchRelation {
  Domestic = 'Domestic',
  Foreign = 'Foreign',
  All = 'All'
}

export enum NoneAuto {
  None = 'None',
  Auto = 'Auto'
}

export enum ParameterLevel {
  COMPANY = 'COMPANY',
  NEDERMAN = 'NEDERMAN',
  USER = 'USER',
  BOTH = 'BOTH'
}

export enum ParameterStatus {
  Optional = 'Optional',
  Required = 'Required',
  Dependent = 'Dependent'
}

export enum ParameterType {
  Integer = 'Integer',
  String = 'String',
  Float = 'Float',
  Enum = 'Enum',
  Boolean = 'Boolean',
  Generated = 'Generated'
}

export enum Status {
  Enabled = 'Enabled',
  Disabled = 'Disabled'
}

export enum SupplierOrderyBy {
  Name = 'Name',
  TenantGroup = 'TenantGroup'
}

export enum UserType {
  Access = 'Access',
  Internal = 'Internal',
  External = 'External'
}

/////// ENTITIES //////
export interface Application {
  applicationId?: string;
  name?: string;
  sharedTenancy?: string;
  multiSupplier?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  instances?: Instance[];
  roles?: ApplicationRole[];
  suppliers?: Supplier[];
  tenants?: Tenant[];
  nedermanGrants?: NedermanGrant[];
  customerGrants?: CustomerGrant[];
  parameters?: ApplicationParameter[];
  customerCompanyCount?: number;
  customerUserCount?: number;
  nedermanUserCount?: number;
}

export interface ApplicationParameter {
  parameterId?: string;
  applicationId?: string;
  name?: string;
  label?: string;
  description?: string;
  level?: string;
  ordinal?: number;
  status?: string;
  parentId?: string;
  type?: string;
  options?: string;
  minValue?: number;
  maxValue?: number;
  maxLength?: number;
  defaultValue?: string;
  expression?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  parent?: ApplicationParameter;
  children?: ApplicationParameter[];
  application?: Application;
  tenantParameters?: TenantParameter[];
  customerParameters?: CustomerParameter[];
  nedermanParameters?: NedermanParameter[];
  value?: string;
}

export interface ApplicationRole {
  roleId?: string;
  applicationId?: string;
  name?: string;
  description?: string;
  companyType?: string;
  userType?: string;
  customerAdmin?: string;
  precedance?: number;
  groupName?: string;
  groupObjectId?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  application?: Application;
  nedermanGrants?: NedermanGrant[];
  customerGrants?: CustomerGrant[];
}

export interface Audit {
  auditId?: string;
  requestId?: string;
  actorType?: string;
  actionId?: string;
  status?: string;
  description?: string;
  response?: string;
  error?: string;
  actorNedermanGrantId?: string;
  actorCustomerGrantId?: string;
  actorNedermanUserId?: string;
  actorCustomerUserId?: string;
  actorApplicationId?: string;
  actorInstanceId?: string;
  actorIpAddress?: string;
  syncId?: string;
  nedermanId?: string;
  customerId?: string;
  nedermanUserId?: string;
  customerUserId?: string;
  nedermanGrantId?: string;
  customerGrantId?: string;
  tenantId?: string;
  tenantGroupId?: string;
  supplierId?: string;
  accountId?: string;
  applicationId?: string;
  instanceId?: string;
  created?: Date;
  updated?: Date;
  version?: number;
}

export interface Customer {
  customerId?: string;
  name?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  telephone?: string;
  fax?: string;
  language?: string;
  currency?: string;
  vat?: string;
  storeNumber?: string;
  segment?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  users?: CustomerUser[];
  suppliers?: Supplier[];
  tenantGroup?: TenantGroup;
  userCount?: number;
  associated?: boolean;
}

export interface CustomerAccount {
  accountId?: string;
  nedermanId?: string;
  siteId?: string;
  accountNumber?: string;
  isBlocked?: string;
  name?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  state?: string;
  area?: string;
  address2?: string;
  city2?: string;
  zipCode2?: string;
  country2?: string;
  state2?: string;
  purchasingReference?: string;
  telephone?: string;
  fax?: string;
  lineOfBusiness?: string;
  salesResponsible?: string;
  termsOfPayment?: string;
  termsOfDelivery?: string;
  language?: string;
  currency?: string;
  priceGroup?: string;
  discountGroup?: string;
  vatNo?: string;
  deletedFromSource?: string;
  lastModified?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  company?: boolean;
  nederman?: Nederman;
}

export interface CustomerGrant {
  grantId?: string;
  userId?: string;
  applicationId?: string;
  instanceId?: string;
  roleId?: string;
  roleName?: string;
  status?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  user?: CustomerUser;
  application?: Application;
  instance?: Instance;
  role?: ApplicationRole;
  parameters?: CustomerParameter[];
  editable?: boolean;
}

export interface CustomerIdentity {
  objectId?: string;
  directoryId?: string;
  userId?: string;
  principalName?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  user?: CustomerUser;
  directory?: Directory;
}

export interface CustomerParameter {
  grantId?: string;
  parameterId?: string;
  value?: string;
  definition?: ApplicationParameter;
  grant?: CustomerGrant;
}

export interface CustomerUser {
  userId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  customerId?: string;
  address?: string;
  city?: string;
  country?: string;
  telephone?: string;
  firstLogin?: Date;
  lastLogin?: Date;
  invited?: Date;
  inviter?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  customer?: Customer;
  identities?: CustomerIdentity[];
  grants?: CustomerGrant[];
  applicationCount?: number;
  editable?: boolean;
}

export interface Directory {
  directoryId?: string;
  tenant?: string;
  senderEmail?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  instances?: Instance[];
  nedermanIdentities?: NedermanIdentity[];
  customerIdentities?: CustomerIdentity[];
}

export interface Instance {
  instanceId?: string;
  applicationId?: string;
  parentId?: string;
  name?: string;
  type?: string;
  nedermanTenant?: string;
  customerAdmin?: string;
  url?: string;
  dataCenter?: string;
  directoryId?: string;
  clientId?: string;
  enterpriseId?: string;
  accessObjectId?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  application?: Application;
  directory?: Directory;
  tenants?: Tenant[];
  nedermanGrants?: NedermanGrant[];
  customerGrants?: CustomerGrant[];
  customerCompanyCount?: number;
  customerUserCount?: number;
  nedermanUserCount?: number;
}

export interface Nederman {
  nedermanId?: string;
  erpDataId?: string;
  name?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  telephone?: string;
  fax?: string;
  language?: string;
  currency?: string;
  vatNo?: string;
  mailboxGLN?: string;
  companyGLN?: string;
  deleteFromSource?: string;
  lastModified?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  users?: NedermanUser[];
  accounts?: CustomerAccount[];
  suppliers?: Supplier[];
  grants?: NedermanGrant[];
  tenantGroup?: TenantGroup;
  userCount?: number;
  invitable?: boolean;
}

export interface NedermanGrant {
  grantId?: string;
  userId?: string;
  applicationId?: string;
  instanceId?: string;
  roleId?: string;
  roleName?: string;
  nedermanId?: string;
  status?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  user?: NedermanUser;
  application?: Application;
  instance?: Instance;
  role?: ApplicationRole;
  nederman?: Nederman;
  parameters?: NedermanParameter[];
}

export interface NedermanIdentity {
  objectId?: string;
  directoryId?: string;
  userId?: string;
  principalName?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  user?: NedermanUser;
  directory?: Directory;
}

export interface NedermanParameter {
  grantId?: string;
  parameterId?: string;
  value?: string;
  definition?: ApplicationParameter;
  grant?: NedermanGrant;
}

export interface NedermanUser {
  userId?: string;
  nedermanId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  jobTitle?: string;
  dirCompany?: string;
  nedermanCompany?: string;
  department?: string;
  office?: string;
  street?: string;
  poBox?: string;
  postalCode?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  mobilePhoneNumber?: string;
  telephoneNumber?: string;
  manager?: string;
  userStatus?: string;
  samAccountName?: string;
  GUID?: string;
  SID?: string;
  creationDate?: string;
  lastModified?: string;
  azureAdObjectId?: string;
  erpDataId?: string;
  siteId?: string;
  firstLogin?: Date;
  lastLogin?: Date;
  assigns?: number;
  invited?: Date;
  inviter?: string;
  division?: string;
  region?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  nederman?: Nederman;
  identities?: NedermanIdentity[];
  grants?: NedermanGrant[];
  applicationCount?: number;
  editable?: boolean;
}

export interface Session {
  sessionId?: string;
  subject?: string;
  userId?: string;
  companyId?: string;
  role?: string;
  scope?: string;
  ipAddress?: string;
  email?: string;
  name?: string;
  previousLogin?: Date;
  issued?: Date;
  expires?: Date;
  discarded?: Date;
  created?: Date;
  updated?: Date;
  version?: number;
}

export interface Store {
  storeId?: string;
  data?: string;
  expiry?: Date;
}

export interface Supplier {
  supplierId?: string;
  nedermanId?: string;
  customerId?: string;
  applicationId?: string;
  accountNumber?: string;
  status?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  application?: Application;
  nederman?: Nederman;
  customer?: Customer;
  associated?: boolean;
}

export interface Sync {
  syncId?: string;
  type?: string;
  status?: string;
  uploadTime?: Date;
  fileName?: string;
  fileSize?: number;
  lineCount?: number;
  discardCount?: number;
  batchCount?: number;
  note?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  batches?: SyncBatch[];
}

export interface SyncBatch {
  syncBatchId?: string;
  syncId?: string;
  batchNo?: number;
  type?: string;
  status?: string;
  fileName?: string;
  etag?: string;
  logStreamName?: string;
  lineCount?: number;
  discardCount?: number;
  createCount?: number;
  updateCount?: number;
  removeCount?: number;
  note?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  sync?: Sync;
}

export interface Tenant {
  tenantId?: string;
  applicationId?: string;
  instanceId?: string;
  tenantGroupId?: string;
  serviceLevel?: string;
  status?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  application?: Application;
  instance?: Instance;
  tenantGroup?: TenantGroup;
  parameters?: TenantParameter[];
  associated?: boolean;
}

export interface TenantGroup {
  tenantGroupId?: string;
  parentGroupId?: string;
  type?: string;
  level?: number;
  name?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  parent?: TenantGroup;
  children?: TenantGroup[];
  customer?: Customer;
  nederman?: Nederman;
  tenants?: Tenant[];
  applicationCount?: number;
}

export interface TenantParameter {
  tenantId?: string;
  parameterId?: string;
  value?: string;
  definition?: ApplicationParameter;
  tenant?: Tenant;
}

//////// INPUTS ///////
export interface AddSupplierCommand {
  customerId?: string;
  accountId?: string;
  nedermanId?: string;
  instanceId?: string;
  accountNumber?: string;
  parameters?: ParameterValue[];
}

export interface AddSupplierPrepareCreate {
  customerId?: string;
  accountId?: string;
}

export interface ApplicationQueryInput {
  search?: string;
  orderBy?: ApplicationOrderyBy;
}

export interface ApplicationRequest {
  applicationId?: string;
  instanceId?: string;
  type?: string;
}

export interface AuditQueryInput {
  applicationId?: string;
  startDate?: Date;
  endDate?: Date;
  scope?: string;
}

export interface AuditQueryInputInst {
  applicationId?: string;
  instanceId?: string;
  startDate?: Date;
  endDate?: Date;
  scope?: string;
}

export interface AzureAssignmentInput {
  dir?: string;
  principalId?: string;
  enterpriseId?: string;
}

export interface AzureDismissInput {
  dir?: string;
  userId?: string;
  assignmentId?: string;
}

export interface AzureGroupIdInput {
  dir?: string;
  groupId?: string;
}

export interface AzureGroupInput {
  dir?: string;
  groupId?: string;
  displayName?: string;
  mailNickname?: string;
}

export interface AzureInviteInput {
  dir?: string;
  email?: string;
  applicationId?: string;
  displayName?: string;
  sendInvitation?: boolean;
  applicationUrl?: string;
}

export interface AzureMembershipInput {
  dir?: string;
  userId?: string;
  groupId?: string;
}

export interface AzureUserIdInput {
  dir?: string;
  userId?: string;
}

export interface AzureUserInput {
  dir?: string;
  userId?: string;
  email?: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  accountEnabled?: boolean;
}

export interface AzureUserQueryInput {
  dir?: string;
  name?: string;
  email?: string;
  accountEnabled?: boolean;
}

export interface CompaniesAccessRequest {
  applicationId?: string;
  type?: string;
  instanceId?: string;
  after?: string;
  ver?: string;
}

export interface CompanyAccessRequest {
  applicationId?: string;
  instanceId?: string;
  companyId?: string;
  ver?: string;
}

export interface CustomerAccountQueryInput {
  nedermanId?: string;
  search?: string;
  orderBy?: string;
  noUnmapped?: boolean;
  offset?: number;
}

export interface CustomerAccountRequest {
  accountId?: string;
}

export interface CustomerCompanyCreateCommand {
  accountId?: string;
  name?: string;
  nedermanId?: string;
  instanceId?: string;
  groupName?: string;
  parameters?: ParameterValue[];
}

export interface CustomerCompanyEditInput {
  customerId?: string;
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  telephone?: string;
  currency?: string;
  fax?: string;
  language?: string;
  state?: string;
  zipCode?: string;
  vat?: string;
}

export interface CustomerCompanyQuery {
  search?: string;
  orderBy?: string;
  applicationId?: string;
  instanceId?: string;
  offset?: number;
}

export interface CustomerCompanyRequest {
  customerId?: string;
}

export interface CustomerGrantAssignCommand {
  userId?: string;
  instanceId?: string;
  roleId?: string;
  status?: Status;
  parameters?: ParameterValue[];
}

export interface CustomerGrantEditCommand {
  grantId?: string;
  status?: Status;
  parameters?: ParameterValue[];
}

export interface CustomerUserEditInput {
  userId?: string;
  customerId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  country?: string;
  telephone?: string;
  created?: Date;
  updated?: Date;
  version?: number;
}

export interface CustomerUserQueryInput {
  search?: string;
  customerId?: string;
  orderBy?: string;
  applicationIds?: string[];
  instanceIds?: string[];
  offset?: number;
}

export interface CustomerUserRequest {
  userId?: string;
}

export interface EditSupplierCommand {
  supplierId?: string;
  customerId?: string;
  applicationId?: string;
  nedermanId?: string;
  status?: Status;
}

export interface EditTenantCommand {
  customerId?: string;
  tenantId?: string;
  applicationId?: string;
  tenantGroupId?: string;
  instanceId?: string;
  status?: Status;
  parameters?: ParameterValue[];
}

export interface InviteUserCommand {
  customerId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  instanceId?: string;
  roleId?: string;
  parameters?: ParameterValue[];
}

export interface InviteUserPrepare {
  customerId?: string;
}

export interface ListSupplierCustomersRequest {
  nedermanId?: string;
  search?: string;
  orderBy?: SupplierOrderyBy;
  offset?: number;
}

export interface NedermanAssignGrantCommand {
  userId?: string;
  instanceId?: string;
  nedermanId?: string;
  roleId?: string;
  status?: Status;
  parameters?: ParameterValue[];
  userObjectId?: string;
}

export interface NedermanCompanyQueryInput {
  search?: string;
  orderBy?: NedermanCompanyOrderBy;
  offset?: number;
  fetchAll?: boolean;
}

export interface NedermanCompanyRequest {
  nedermanId?: string;
}

export interface NedermanEditGrantCommand {
  grantId?: string;
  status?: Status;
  parameters?: ParameterValue[];
}

export interface NedermanListCompaniesRequest {
  assigned?: string;
  hostNedermanId?: string;
}

export interface NedermanUserEditInput {
  userId?: string;
  nedermanId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  jobTitle?: string;
  dirCompany?: string;
  nedermanCompany?: string;
  department?: string;
  office?: string;
  street?: string;
  poBox?: string;
  postalCode?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  mobilePhoneNumber?: string;
  telephoneNumber?: string;
  manager?: string;
  userStatus?: string;
  samAccountName?: string;
  GUID?: string;
  SID?: string;
  creationDate?: string;
  lastModified?: string;
  firstLogin?: Date;
  lastLogin?: Date;
  assigns?: number;
  invited?: Date;
  inviter?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  azureAdObjectId?: string;
  erpDataId?: string;
  siteId?: string;
}

export interface NedermanUserInviteCommand {
  nedermanId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  instanceId?: string;
  roleId?: string;
  parameters?: ParameterValue[];
}

export interface NedermanUserInvitePrepare {
  nedermanId?: string;
}

export interface NedermanUserRequest {
  userId?: string;
  objectId?: string;
}

export interface NedermanUserSearchInput {
  search?: string;
  company?: string;
  hostNedermanId?: string;
  foreignNedermanId?: string;
  userRelation?: NedermanUserSearchRelation;
  assign?: NedermanUserSearchAssign;
  orderBy?: NedermanUserSearchOrderBy;
  applicationId?: string;
  instanceId?: string;
  offset?: number;
}

export interface ParametersValidationRequest {
  applicationId?: string;
  level?: string;
  parameters?: ParameterValue[];
}

export interface ParameterValue {
  parameterId?: string;
  value?: any;
}

export interface SaveGrantParametersRequest {
  grantId?: string;
  applicationId?: string;
  type?: string;
  parameters?: ParameterValue[];
}

export interface SaveTenantParametersRequest {
  tenantId?: string;
  customerId?: string;
  applicationId?: string;
  parameters?: ParameterValue[];
}

export interface SelfRegistrationRequest {
  customerId?: string;
  applicationId?: string;
  instanceId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  roleId?: string;
}

export interface SupplierSaveCommand {
  applicationId?: string;
  customerId?: string;
  nedermanId?: string;
  accountNumber?: string;
  accountId?: string;
}

export interface SyncQueryInput {
  startDate?: Date;
  endDate?: Date;
}

export interface TenantCreateCommand {
  applicationId?: string;
  instanceId?: string;
  tenantGroupId?: string;
}

export interface TenantGroupCreateCommand {
  groupName?: string;
}

export interface UserAccessRequest {
  applicationId?: string;
  instanceId?: string;
  userId?: string;
  ver?: string;
}

export interface UsersAccessRequest {
  applicationId?: string;
  type?: string;
  instanceId?: string;
  after?: string;
  ver?: string;
  filter?: any;
}

//////// TYPES ////////
export interface AddSupplierPrepareAdvice {
  customerId?: string;
  accountId?: string;
  name?: string;
  supplierName?: string;
  nedermanId?: string;
  accountNumber?: string;
  applications?: ApplicationAdvice[];
}

export interface AddSupplierResult {
  tenant?: Tenant;
  supplier?: SupplierItem;
}

export interface ApplicationAdvice {
  applicationId?: string;
  name?: string;
  roles?: ApplicationRoleListItem[];
  instances?: InstanceListItem[];
  parameters?: ParameterInstance[];
}

export interface ApplicationList {
  query?: any;
  items?: ApplicationListItem[];
}

export interface ApplicationListItem {
  applicationId?: string;
  instanceId?: string;
  name?: string;
  instance?: string;
  roles?: ApplicationRoleListItem[];
  parameters?: ApplicationParameter[];
  customerCompanyCount?: number;
  customerUserCount?: number;
  nedermanUserCount?: number;
}

export interface ApplicationOverview {
  applicationId?: string;
  name?: string;
  sharedTenancy?: string;
  multiSupplier?: string;
  parameters?: ApplicationParameter[];
  created?: Date;
  updated?: Date;
  version?: number;
  instance?: Instance;
}

export interface ApplicationRoleListItem {
  roleId?: string;
  name?: string;
  description?: string;
  companyType?: string;
  customerAdmin?: string;
}

export interface AuditList {
  query?: any;
  items?: AuditListItem[];
  scope?: boolean;
}

export interface AuditListItem {
  created?: Date;
  requestId?: string;
  actionId?: string;
  actorType?: string;
  description?: string;
  actorIpAddress?: string;
  status?: string;
}

export interface AzureAssignmentResult {
  principalId?: string;
  enterpriseId?: string;
  assignmentId?: string;
  principalDisplayName?: string;
  appDisplayName?: string;
  debug?: any;
}

export interface AzureDismissResult {
  userId?: string;
  assignmentId?: string;
  debug?: any;
}

export interface AzureGroupMemberResult {
  dir?: string;
  userId?: string;
  groupId?: string;
  debug?: any;
}

export interface AzureGroupResult {
  groupId?: string;
  displayName?: string;
  description?: string;
  mailNickname?: string;
  mailEnabled?: boolean;
  mail?: string;
  securityEnabled?: boolean;
  createdDateTime?: string;
  deletedDateTime?: string;
  renewedDateTime?: string;
  deleted?: boolean;
  debug?: any;
}

export interface AzureInviteResult {
  userId?: string;
  userPrincipalName?: string;
  email?: string;
  applicationId?: string;
  displayName?: string;
  sendInvitation?: boolean;
  applicationUrl?: string;
  redirectUrl?: string;
  inviteId?: string;
  redeemUrl?: string;
  status?: string;
  debug?: any;
}

export interface AzureUserInfo {
  userId?: string;
  email?: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  accountEnabled?: boolean;
  userPrincipalName?: string;
  userType?: string;
  initialPassword?: string;
  deleted?: boolean;
  debug?: any;
}

export interface AzureUserQueryResult {
  query?: any;
  users?: AzureUserInfo[];
  debug?: any;
}

export interface BatchItem {
  note?: string;
}

export interface CompanyAccessInfo {
  type?: string;
  companyId?: string;
  companyName?: string;
  erpDataId?: string;
  siteId?: string;
  accountNumber?: string;
  updated?: Date;
  companyPhone?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  tenants?: TenantAccessInfo[];
}

export interface CustomerAccountList {
  query?: any;
  items?: CustomerAccountListItem[];
  scope?: boolean;
}

export interface CustomerAccountListItem {
  accountId?: string;
  nedermanId?: string;
  siteId?: string;
  accountNumber?: string;
  name?: string;
  city?: string;
  country?: string;
  vat?: string;
  version?: number;
  created?: Date;
  updated?: Date;
}

export interface CustomerAccountOverview {
  accountId?: string;
  siteId?: string;
  nedermanId?: string;
  accountNumber?: string;
  isBlocked?: string;
  name?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  state?: string;
  area?: string;
  address2?: string;
  city2?: string;
  zipCode2?: string;
  country2?: string;
  state2?: string;
  purchasingReference?: string;
  telephone?: string;
  fax?: string;
  lineOfBusiness?: string;
  salesResponsible?: string;
  termsOfPayment?: string;
  termsOfDelivery?: string;
  language?: string;
  currency?: string;
  priceGroup?: string;
  discountGroup?: string;
  vatNo?: string;
  deletedFromSource?: string;
  lastModified?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  company?: boolean;
}

export interface CustomerCompanyCreateResult {
  company?: Customer;
  group?: TenantGroupCreateResult;
  tenant?: Tenant;
  supplier?: SupplierItem;
}

export interface CustomerCompanyList {
  query?: any;
  items?: CustomerCompanyListItem[];
}

export interface CustomerCompanyListItem {
  customerId?: string;
  name?: string;
  tenantGroupId?: string;
  tenantGroupName?: string;
  country?: string;
  vat?: string;
  applicationCount?: number;
  userCount?: number;
  version?: number;
  created?: Date;
  updated?: Date;
}

export interface CustomerCompanyOverview {
  customerId?: string;
  name?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  telephone?: string;
  fax?: string;
  language?: string;
  currency?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  tenantGroup?: TenantGroupItem;
  suppliers?: SupplierItem[];
  associated?: boolean;
}

export interface CustomerCompanyPrepareCreateAdvice {
  accountId?: string;
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  nedermanId?: string;
  supplierName?: string;
  groupName?: string;
  applications?: ApplicationAdvice[];
}

export interface CustomerGrantPrepareAssignAdvice {
  userId?: string;
  email?: string;
  displayName?: string;
  companyName?: string;
  applications?: ApplicationAdvice[];
}

export interface CustomerUserGrant {
  grantId?: string;
  userId?: string;
  applicationId?: string;
  instanceId?: string;
  roleId?: string;
  roleName?: string;
  status?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  instance?: Instance;
  role?: ApplicationRole;
  parameters?: ParameterInstance[];
  editable?: boolean;
}

export interface CustomerUserList {
  query?: any;
  items?: CustomerUserListItem[];
}

export interface CustomerUserListItem {
  userId?: string;
  email?: string;
  name?: string;
  customerId?: string;
  customerName?: string;
  applicationCount?: number;
  version?: number;
  created?: Date;
  updated?: Date;
}

export interface CustomerUserOverview {
  userId?: string;
  customerId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  country?: string;
  telephone?: string;
  invited?: Date;
  inviter?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  customer?: Customer;
  grants?: CustomerUserGrant[];
  editable?: boolean;
}

export interface GrantAccessInfo {
  grantId?: string;
  roleId?: string;
  roleName?: string;
  instanceId?: string;
  instanceUrl?: string;
  companyId?: string;
  companyName?: string;
  tenantId?: string;
  status?: string;
  updated?: Date;
}

export interface InstanceListItem {
  instanceId?: string;
  name?: string;
}

export interface InviteUserAdvice {
  companyName?: string;
  customerId?: string;
  applications?: ApplicationAdvice[];
}

export interface InviteUserResult {
  customerId?: string;
  userId?: string;
  email?: string;
  applicationId?: string;
  instanceId?: string;
  roleId?: string;
}

export interface LoginGrantItem {
  grantId?: string;
  nedermanId?: string;
  roleId?: string;
  roleName?: string;
  label?: string;
  selected?: boolean;
}

export interface LoginGrantList {
  userId?: string;
  grants?: LoginGrantItem[];
}

export interface NedermanApplication {
  applicationId?: string;
  name?: string;
  roles?: ApplicationRoleListItem[];
}

export interface NedermanAssignedCompaniesList {
  items?: NedermanCompanyListItem[];
}

export interface NedermanAssignGrantAdvice {
  userId?: string;
  nedermanId?: string;
  firstName?: string;
  lastName?: string;
  nedermanCompany?: string;
  nedermanCompanies?: NedermanCompanyListItem[];
  applications?: ApplicationAdvice[];
}

export interface NedermanCompanyItem {
  nedermanId?: string;
  erpDataId?: string;
  name?: string;
  city?: string;
  country?: string;
  userCount?: number;
  version?: number;
  created?: Date;
  updated?: Date;
}

export interface NedermanCompanyList {
  query?: NedermanCompanyQueryType;
  items?: NedermanCompanyItem[];
}

export interface NedermanCompanyListItem {
  nedermanId?: string;
  nedermanCompany?: string;
  applications?: ApplicationAdvice[];
}

export interface NedermanCompanyQueryType {
  search?: string;
  orderBy?: string;
  offset?: number;
}

export interface NedermanGrantItem {
  grantId?: string;
  userId?: string;
  applicationId?: string;
  instanceId?: string;
  instance?: Instance;
  nedermanId?: string;
  roleId?: string;
  roleName?: string;
  status?: string;
  nedermanCompany?: Nederman;
  role?: ApplicationRole;
  version?: number;
  created?: Date;
  updated?: Date;
  editable?: boolean;
  parameters?: ParameterInstance[];
}

export interface NedermanSearchResult {
  query?: any;
  items?: NedermanUserInfo[];
}

export interface NedermanUserInfo {
  userId?: string;
  email?: string;
  name?: string;
  nedermanId?: string;
  nedermanName?: string;
  country?: string;
  applicationCount?: number;
  invited?: Date;
  version?: number;
  created?: Date;
  updated?: Date;
}

export interface NedermanUserInviteAdvice {
  companyName?: string;
  nedermanId?: string;
  applications?: ApplicationAdvice[];
}

export interface NedermanUserInviteResult {
  nedermanId?: string;
  userId?: string;
  email?: string;
  applicationId?: string;
  instanceId?: string;
  roleId?: string;
}

export interface NedermanUserOverview {
  userId?: string;
  nedermanId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  jobTitle?: string;
  dirCompany?: string;
  nedermanCompany?: string;
  department?: string;
  office?: string;
  street?: string;
  poBox?: string;
  postalCode?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  mobilePhoneNumber?: string;
  telephoneNumber?: string;
  manager?: string;
  userStatus?: string;
  samAccountName?: string;
  GUID?: string;
  SID?: string;
  creationDate?: string;
  lastModified?: string;
  firstLogin?: Date;
  lastLogin?: Date;
  assigns?: number;
  invited?: Date;
  inviter?: string;
  created?: Date;
  updated?: Date;
  version?: number;
  azureAdObjectId?: string;
  erpDataId?: string;
  siteId?: string;
  editable?: boolean;
  grants?: NedermanGrantItem[];
}

export interface ParameterInstance {
  parameterId?: string;
  value?: any;
  definition?: ApplicationParameter;
}

export interface ParameterValueItem {
  parameterId?: string;
  grantId?: string;
  value?: any;
}

export interface SelfRegistrationPrepareAdvice {
  customerId?: string;
  customerName?: string;
  applicationId?: string;
  applicationName?: string;
  instanceId?: string;
  instanceUrl?: string;
  roleId?: string;
}

export interface SelfRegistrationResult {
  email?: string;
  companyName?: string;
}

export interface SessionInfo {
  sessionId?: string;
  issuer?: string;
  audience?: string;
  subject?: string;
  remote?: boolean;
  userId?: string;
  role?: string;
  scope?: string;
  companyId?: string;
  email?: string;
  name?: string;
  ipAddress?: string;
  previousLogin?: Date;
  issued?: Date;
  expires?: Date;
  token?: string;
}

export interface SimpleApplicationItem {
  applicationId?: string;
  name?: string;
}

export interface SupplierCustomerCompaniesList {
  query?: any;
  items?: CustomerCompanyListItem[];
}

export interface SupplierItem {
  supplierId?: string;
  applicationId?: string;
  customerId?: string;
  nedermanId?: string;
  accountNumber?: string;
  status?: Status;
  nedermanCompany?: Nederman;
  version?: number;
  created?: Date;
  upldated?: Date;
  associated?: boolean;
}

export interface SyncList {
  query?: any;
  items?: SyncListItem[];
}

export interface SyncListItem {
  syncId?: string;
  type?: string;
  lineCount?: number;
  discardCount?: number;
  batchCount?: number;
  status?: string;
  created?: Date;
  batch?: BatchItem[];
}

export interface TenantAccessInfo {
  instanceId?: string;
  instanceUrl?: string;
  tenantId?: string;
  status?: string;
  updated?: Date;
}

export interface TenantCreateResult {
  tenantId?: string;
  applicationId?: string;
  instanceId?: string;
  tenantGroupId?: string;
  serviceLevel?: string;
  status?: Status;
}

export interface TenantGroupCreateResult {
  group?: TenantGroupItem;
  proxy?: TenantGroupItem;
}

export interface TenantGroupItem {
  tenantGroupId?: string;
  tenantParentId?: string;
  name?: string;
  type?: string;
  level?: number;
  tenants?: Tenant[];
}

export interface TenantParameterValueItem {
  parameterId?: string;
  tenantId?: string;
  value?: any;
  definition?: ApplicationParameter;
}

export interface UserAccessInfo {
  type?: string;
  userId?: string;
  objectId?: string;
  userObjectId?: string;
  principalName?: string;
  companyId?: string;
  companyName?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  companyPhone?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  storeNumber?: string;
  segment?: string;
  jobTitle?: string;
  division?: string;
  manager?: string;
  region?: string;
  department?: string;
  grants?: GrantAccessInfo[];
}
